import { createRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Ref, Sticky } from 'semantic-ui-react'
import { DateTime } from 'luxon'

// BL
import {
  fetchSingleAccountReconciliationIfNeeded,
  getFetchSingleAccountRecKey,
  performManualReconciliation,
} from '@/actions/admin/accountReconciliationActions'
import {
  adminDeleteTransaction,
  BULK_UPDATE_USER_TRANSACTIONS_KEY,
  bulkUpdateUserTransactions,
} from '@/actions/adminActions'
import {
  FETCH_FINANCIAL_ACCOUNT_TRANSACTION_SUM_KEY,
  fetchFinancialAccountTransactionSum,
  FinancialAccountSumResponse,
} from '@/actions/admin/adminFinancialAccountActions'
import {
  FETCH_TRANSACTION_CATEGORIES_KEY,
  fetchAdminTransactionCategoriesIfNeeded,
} from '@/features/reports/reports.slice'
import { Alert } from '@/ui'
import { TransactionRowModel } from '../shared'
import { parseWrongPeriodRejection, wrongPeriodMessage } from '../wrongPeriod'
import { generateTableDatasource } from '../service'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { isAxiosError } from 'axios'

// UI
import '../style.scss'
import PanelHeader from '../Components/PanelHeader'
import PanelToolbar from '../Components/PanelToolbar'
import PanelTable from '../Components/PanelTable'
import TransactionModal from '../Components/PanelTransactionModal'
import { getReconciliationById } from '@/selectors/reconciliations.selectors'
import { useReselector } from '@/utils/sharedHooks'
import {
  getFetchError,
  selectErrorsForKeys,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { centsToCurrency } from '@/utils/currencyHelpers'
import { ENABLE_ADMIN, useAnalyticsTrack } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import { parseErrorFromCatch } from '@/utils/errorHelpers'
import {
  fetchAllAdminTransactionPaginated,
  FETCH_ALL_ADMIN_TRANSACTION_PAGINATED_KEY,
} from '@/actions/admin/v2/adminTransactionActions'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { UserFreeTrialAlert } from '@/features/admin/user-record/UserAccountStates/UserFreeTrialAlert'

const ReconciliationPanel = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack(ENABLE_ADMIN)
  const contextRef = createRef<HTMLElement>()
  const [account, setAccount] = useState<FinancialAccountSumResponse | null>(
    null
  )
  const [addTransactionCnt, setAddTransactionCnt] = useState<number>(0)
  const [deleteTransactionCnt, setDeleteTransactionCnt] = useState<number>(0)
  const [completeError, setCompleteError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Not in `errors` below: that list blanks the whole panel, which a refused row action must not.
  const [rowActionError, setRowActionError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { userId, reconciliationId = '0' } = useParams()
  const reconciliation = useReselector(getReconciliationById, reconciliationId)
  const fetchAccountRecKey = getFetchSingleAccountRecKey(reconciliationId)

  const loading = useReselector(selectIsFetchingForKeys, [
    FETCH_TRANSACTION_CATEGORIES_KEY,
    fetchAccountRecKey,
    FETCH_ALL_ADMIN_TRANSACTION_PAGINATED_KEY,
    FETCH_FINANCIAL_ACCOUNT_TRANSACTION_SUM_KEY,
  ])

  const bulkUpdateFetchError = useReselector(
    getFetchError,
    BULK_UPDATE_USER_TRANSACTIONS_KEY
  )

  const errors = useReselector(selectErrorsForKeys, [
    fetchAccountRecKey,
    FETCH_ALL_ADMIN_TRANSACTION_PAGINATED_KEY,
  ])
  const [transactions, setTransactions] = useState<Transaction[]>([])

  const datasource = useMemo(
    () =>
      reconciliation
        ? generateTableDatasource(reconciliation, transactions)
        : [],
    [reconciliation, transactions]
  )
  const startDate = useMemo(
    () =>
      reconciliation?.startingBalanceDate
        ? DateTime.fromISO(reconciliation.startingBalanceDate, { zone: 'utc' })
        : undefined,
    [reconciliation?.startingBalanceDate]
  )
  const endDate = useMemo(
    () =>
      reconciliation?.endingBalanceDate
        ? DateTime.fromISO(reconciliation.endingBalanceDate, { zone: 'utc' })
        : undefined,
    [reconciliation?.endingBalanceDate]
  )

  // Initial fetches
  useEffect(() => {
    dispatch(fetchAdminTransactionCategoriesIfNeeded())
    dispatch(fetchSingleAccountReconciliationIfNeeded(reconciliationId))
  }, [dispatch, reconciliationId])

  useEffect(() => {
    const fetch = async ({
      startDate,
      endDate,
      userId,
    }: {
      startDate: string
      endDate: string
      userId: number
    }) => {
      const transactions = await dispatch(
        fetchAllAdminTransactionPaginated({
          filter: {
            startDate,
            endDate,
            userId,
            financialAccountId: reconciliation?.accountId,
            includePersonal: true,
          },
          orderBy: {
            column: 'date',
            direction: 'descending',
          },
        })
      )
      setTransactions(transactions)
    }

    if (!startDate || !endDate || !userId) {
      return
    }

    fetch({
      startDate: startDate.toISO() ?? '',
      endDate: endDate.toISO() ?? '',
      userId: Number(userId),
    })
  }, [
    dispatch,
    endDate,
    reconciliation?.accountId,
    startDate,
    userId,
    addTransactionCnt,
    deleteTransactionCnt,
  ])

  // Fetch for transaction sum
  useEffect(() => {
    const fetch = async () => {
      if (!startDate || !endDate || !reconciliation?.accountId) {
        return
      }
      const accountResult = await fetchFinancialAccountTransactionSum(
        reconciliation?.accountId,
        startDate,
        endDate
      )(dispatch)

      if (accountResult?.inactive) {
        setLoadError(
          'This account is inactive, so its transactions were not loaded. The Heard balance below is not a real total — do not complete this reconciliation from it.'
        )
        return
      }
      setLoadError(null)
      if (accountResult) {
        setAccount(accountResult)
      }
    }

    // Rejections from a detached promise reach no error boundary, so a failure here
    // would otherwise leave the panel showing a stale balance as if nothing broke.
    fetch().catch((err) => setLoadError(parseErrorFromCatch(err)))
  }, [reconciliation?.accountId, transactions, startDate, endDate, dispatch])

  const onCheckButtonClicked = useCallback(
    async (t: TransactionRowModel) => {
      setRowActionError(null)
      const data = {
        transactionIds: [t.id],
        reconciliationId: t.matched ? null : t.reconciliationId,
      }
      await bulkUpdateUserTransactions(data)(dispatch)
    },
    [dispatch]
  )

  const onDeleteClicked = useCallback(
    async (t: TransactionRowModel) => {
      setRowActionError(null)
      try {
        await adminDeleteTransaction(Number(t.id))(dispatch)
      } catch (err) {
        setRowActionError(parseErrorFromCatch(err))
        return
      }
      setDeleteTransactionCnt((prev) => prev + 1)
    },
    [dispatch, setDeleteTransactionCnt]
  )

  const onAddTransaction = useCallback(() => {
    setAddTransactionCnt((prev) => prev + 1)
  }, [])

  const onCompleteClicked = useCallback(async () => {
    // Mark reconciliation complete
    setCompleteError(null)

    if (!account) {
      throw new Error('Account does not exist')
    }

    if (!reconciliation?.id) {
      throw new Error('Reconciliation does not exist')
    }

    const { bookkeepingReportId } = reconciliation
    if (bookkeepingReportId === undefined) {
      setCompleteError(
        'This reconciliation is not attached to a bookkeeping report, so it cannot be completed.'
      )
      return
    }

    const asDateString = (isoDate: string) =>
      DateTime.fromISO(isoDate, { zone: 'utc' }).toFormat(
        DATE_FORMATS_LUXON.YEAR_MONTH_DAY
      )

    let result
    try {
      result = await performManualReconciliation(account.id, {
        bookkeepingReportId,
        startingBalanceDate: asDateString(reconciliation.startingBalanceDate),
        startingBalanceInCents: reconciliation.startingBalanceInCents ?? 0,
        endingBalanceDate: asDateString(reconciliation.endingBalanceDate),
        endingBalanceInCents: reconciliation.endingBalanceInCents,
        bookkeepingProcess: reconciliation.bookkeepingProcess ?? 'monthEnd',
      })(dispatch)
    } catch (err) {
      const rejection = parseWrongPeriodRejection(err)
      // Surface the server's own conflict message (process mismatch, closed books,
      // …) so each 409 states its real cause and recovery.
      setCompleteError(
        rejection
          ? wrongPeriodMessage(rejection)
          : (isAxiosError(err) && err.response?.data?.message) ||
              'Could not complete this reconciliation. Please try again.'
      )
      return
    }

    const { reconciliation: completed, outcome } = result

    // `reuseSettled` means the writer kept an existing settled row untouched (a
    // sibling, or a concurrent completion) — this row was not completed.
    if (outcome === 'reuseSettled') {
      setCompleteError(
        'A different reconciliation already holds this period, so this one was not completed.'
      )
      return
    }

    if (completed.status !== 'complete') {
      setCompleteError(
        'This reconciliation still does not tie out. Adjust the transactions so the Heard balance matches the statement balance, then try again.'
      )
      return
    }

    track('Reconciliation Break Fixed', {
      reconciliationId: reconciliation.id,
      accountId: account.id,
      startDate: reconciliation.startingBalanceDate,
      endDate: reconciliation.endingBalanceDate,
      addTransactionCnt,
      deleteTransactionCnt,
    })

    navigate(-1)
  }, [
    account,
    dispatch,
    reconciliation,
    navigate,
    addTransactionCnt,
    deleteTransactionCnt,
    track,
  ])

  // Signed, for liabilities too: the server tie-out is `sum - (ending - starting)` with
  // no account-type branch, so an absolute value here disagrees with it by twice the sum.
  const transactionSumInCents = useMemo(
    () => Number(account?.transactionsSumInCents || 0),
    [account?.transactionsSumInCents]
  )

  const heardBalanceInCents = useMemo(
    () =>
      centsToCurrency(reconciliation?.startingBalanceInCents).add(
        centsToCurrency(transactionSumInCents)
      ).intValue,
    [reconciliation?.startingBalanceInCents, transactionSumInCents]
  )

  return (
    <>
      {Boolean(errors.length) && <div>{errors[0].message}</div>}
      {!errors.length && loading && <div>Loading</div>}

      {!errors.length && !loading && reconciliation && (
        <Ref innerRef={contextRef}>
          <Container id="FinancialAccountReconciliation">
            <UserFreeTrialAlert userId={userId} />
            <Sticky context={contextRef}>
              <PanelToolbar
                account={account}
                onBackClicked={() => navigate(-1)}
                reconciliation={reconciliation}
              />
              <PanelHeader
                onCompleteClicked={onCompleteClicked}
                onNewTransactionClicked={() => setModalOpen((prev) => !prev)}
                heardBalanceInCents={heardBalanceInCents}
                statementBalanceInCents={reconciliation.endingBalanceInCents}
                locked={reconciliation.status === 'complete'}
              />
              {loadError && <Alert variant="error">{loadError}</Alert>}
              {completeError && <Alert variant="error">{completeError}</Alert>}
              {bulkUpdateFetchError && (
                <Alert variant="error">{bulkUpdateFetchError.message}</Alert>
              )}
              {rowActionError && (
                <Alert variant="error">{rowActionError}</Alert>
              )}
            </Sticky>
            <PanelTable
              datasource={datasource}
              onDeleteClicked={onDeleteClicked}
              onRowChecked={onCheckButtonClicked}
              locked={reconciliation.status === 'complete'}
            />

            <TransactionModal
              userId={Number(userId)}
              reconciliation={reconciliation}
              account={account}
              close={() => setModalOpen(false)}
              open={modalOpen}
              onAddTransaction={onAddTransaction}
            />
          </Container>
        </Ref>
      )}
    </>
  )
}

export default ReconciliationPanel
